import type { VaultItem } from '../types'

/**
 * 一覧の subtitle 行で同じ username (または「username なし」) を持つ login
 * アイテムが複数並ぶときに、識別用の `#2 / #3 …` suffix を計算する。
 *
 * ookey-browser の `disambiguateLoginLabels` と挙動を揃える:
 *   - login type のみ対象 (他 type は subtitle が name 以外の情報になるため触らない)
 *   - 同じ subtitle key を持つ 2 件目以降に `#N` を付ける (1 件目は付けない)
 *   - subtitle key:
 *       - username (前後空白 trim 後) があればそれ自体
 *       - 空 / undefined のときは sentinel `__no_username__` (= 「ユーザー名なし」枠を共有)
 *
 * 例:
 *   items = [
 *     { id:'A', login:{username:'rim@x.com'} },
 *     { id:'B', login:{username:'rim@x.com'} },
 *     { id:'C', login:{username:''} },
 *     { id:'D', login:{username:''} },
 *   ]
 *   返り値: Map { 'A' => '', 'B' => ' #2', 'C' => '', 'D' => ' #2' }
 *
 * 戻り値の Map に **載らない** id (= 重複が無い 1 件目 or non-login) には suffix
 * 不要を意味する。呼出側は `map.get(id) ?? ''` で展開する想定。
 */

const NO_USERNAME_KEY = '__no_username__'

const subtitleKeyOf = (item: VaultItem): string | null => {
  if (item.payload.type !== 'login') return null
  const u = (item.payload.username ?? '').trim()

  return u === '' ? NO_USERNAME_KEY : u
}

export const disambiguateLoginLabels = (items: readonly VaultItem[]): Map<string, string> => {
  const counterByKey = new Map<string, number>()
  const suffixById = new Map<string, string>()

  for (const item of items) {
    const key = subtitleKeyOf(item)
    if (key === null) continue
    const next = (counterByKey.get(key) ?? 0) + 1
    counterByKey.set(key, next)
    if (next >= 2) suffixById.set(item.id, ` #${next}`)
  }

  return suffixById
}
